import { useEffect, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { userIsReadOnly, UserSession } from "./auth/AuthService";
import { useStateUrlParamsCaseType } from "./common/hooks/useStateUrlParams";
import { CaseType } from "./core/cases/Case.types";
import { CostAccessLevel, SelectedOrganization } from "./core/organizations/Organization.types";
import { userHasCostAccess } from "./core/organizations/OrganizationService";
import TopBar, { TopBarDropdownConfig } from "./core/topBar/TopBar";
import { MenuAccess } from "./core/users/User.types";
import useMainStyles from "./Main.styles";
import Admin from "./pages/admin/Admin";
import Copyrights from "./pages/copyrights/Copyrights";
import Costs from "./pages/costs/Costs";
import Dashboard from "./pages/dashboard/Dashboard";
import Designs from "./pages/designs/Designs";
import Disputes from "./pages/disputes/Disputes";
import Organisations from "./pages/organisations/Organisations";
import PriceCalculator from "./pages/priceCalculator/PriceCalculator";
import Renewals from "./pages/renewals/Renewals";
import Surveys from "./pages/surveys/Surveys";
import { SurveyArea } from "./pages/surveys/Surveys.types";
import Trademarks from "./pages/trademarks/Trademarks";
import Users from "./pages/users/Users";
import Watches from "./pages/watches/Watches";
import WatchPrices from "./pages/watchPrices/WatchPrices";

type MainProps = {
    onDrawerExpandedMobile: () => void;
    onSelectedOrgChanged: () => void;
    userSession: UserSession;
};

export type CaseSummarizationProps = {
    onCaseFilterKeyChanged: (caseFilterKeyChange: string) => void;
    onCaseTypeFilterConfigurationCleared: () => void;
    selectedCaseType: CaseType;
    orgHasBeenSelected?: boolean;
    userCostAccess?: CostAccessLevel | null;
    userSession: UserSession;
    renewalRegistrationActive: boolean;
};

/**
 * Check if user has access to a specific menu item
 * Empty array = all access
 * Non-empty array = restricted to specified menu items
 */
const userHasMenuAccess = (user: UserSession["verifiedUser"], menuItem: MenuAccess): boolean => {
    if (!user?.menuAccess) return true; // No restrictions defined
    if (user.menuAccess.length === 0) return true; // Empty array = all access
    return user.menuAccess.includes(menuItem);
};

/**
 * Map CaseType to MenuAccess enum value
 */
const caseTypeToMenuAccess = (caseType: CaseType): MenuAccess => {
    switch (caseType) {
        case CaseType.Trademark:
            return MenuAccess.Trademarks;
        case CaseType.Design:
            return MenuAccess.Designs;
        case CaseType.Copyright:
            return MenuAccess.Copyrights;
        case CaseType.Watches:
            return MenuAccess.Watches;
        default:
            return MenuAccess.Trademarks;
    }
};

const getAvailableCaseTypesFromOrg = (
    selectedOrg: SelectedOrganization | null,
    renewalsIsActive: boolean,
    disputesIsActive: boolean,
    priceCalculatorIsActive: boolean,
    user: UserSession["verifiedUser"]
): CaseType[] => {
    let caseTypes: CaseType[] = [];
    if (!selectedOrg) return caseTypes;
    if (selectedOrg.hasTrademarks) caseTypes.push(CaseType.Trademark);
    if (selectedOrg.hasDesigns) caseTypes.push(CaseType.Design);
    if (selectedOrg.hasWatches && !disputesIsActive) caseTypes.push(CaseType.Watches);
    if (priceCalculatorIsActive) {
        caseTypes = [CaseType.Trademark, CaseType.Design, CaseType.Watches];
    } else {
        if (disputesIsActive && !selectedOrg.hasTrademarkDisputes) caseTypes = caseTypes.filter((caseType) => caseType !== CaseType.Trademark);
        if (disputesIsActive && !selectedOrg.hasDesignDisputes) caseTypes = caseTypes.filter((caseType) => caseType !== CaseType.Design);
        if (selectedOrg.hasCopyrights && !renewalsIsActive && !disputesIsActive) caseTypes.push(CaseType.Copyright);
    }
    // Filter by user menu access permissions
    caseTypes = caseTypes.filter((caseType) => userHasMenuAccess(user, caseTypeToMenuAccess(caseType)));
    return caseTypes;
};

function Main(props: MainProps) {
    const classes = useMainStyles();
    const history = useHistory();

    const [caseType, setSelectedCaseType] = useStateUrlParamsCaseType("caseType", CaseType.Trademark);
    const [caseTypeFilterKey, setCaseTypeFilterKey] = useState<string>();
    const [disputesIsActive, setDisputesIsActive] = useState(false);
    const [orgHasDisputes, setOrgHasDisputes] = useState(false);
    const [renewalsIsActive, setRenewalsIsActive] = useState(false);
    const [priceCalculatorIsActive, setPriceCalculatorIsActive] = useState(false);

    const selectedCaseType = caseType ?? CaseType.Trademark;
    let caseTypeFilterConfig: TopBarDropdownConfig<CaseType> | undefined = undefined;

    const availableCaseTypes = getAvailableCaseTypesFromOrg(
        props.userSession.selectedOrg,
        renewalsIsActive,
        disputesIsActive,
        priceCalculatorIsActive,
        props.userSession.verifiedUser
    );

    const onCaseTypeFilterCleared = () => {
        setCaseTypeFilterKey(undefined);
    };

    useEffect(() => {
        toggleOrgHasDisputes(selectedCaseType);
        // eslint-disable-next-line
    }, []);

    const toggleOrgHasDisputes = (caseType: CaseType) => {
        switch (caseType) {
            case CaseType.Trademark:
                setOrgHasDisputes(!!props.userSession.selectedOrg?.hasTrademarkDisputes);
                break;
            case CaseType.Design:
                setOrgHasDisputes(!!props.userSession.selectedOrg?.hasDesignDisputes);
                break;
            case CaseType.Copyright:
                setOrgHasDisputes(false);
                break;
            default:
                setOrgHasDisputes(false);
        }
    };

    const onSelectedCaseTypeChanged = (changedCaseType: CaseType) => {
        toggleOrgHasDisputes(changedCaseType);
        setSelectedCaseType(changedCaseType);
    };

    useEffect(() => {
        history.listen((location) => {
            const locationState = location.state as { caseType: CaseType };
            if (locationState && locationState.caseType) setSelectedCaseType(locationState.caseType);
        });
        // eslint-disable-next-line
    }, []);

    if (caseTypeFilterKey) {
        caseTypeFilterConfig = {
            disabled: availableCaseTypes.length === 1 ? true : undefined,
            key: caseTypeFilterKey || "",
            onChange: (selectedValue) => onSelectedCaseTypeChanged(selectedValue || CaseType.Trademark),
            options: availableCaseTypes.map((caseType) => ({
                label: caseType,
                value: caseType,
            })),
            value: {
                label: selectedCaseType,
                value: selectedCaseType,
            },
        };
    }

    let dashBoard = (
        <>
            {props.userSession.selectedOrg ? (
                <Dashboard
                    onCaseTypeFilterConfigurationCleared={onCaseTypeFilterCleared}
                    onCaseFilterKeyChanged={setCaseTypeFilterKey}
                    orgHasBeenSelected={!!props.userSession.selectedOrg}
                    selectedCaseType={selectedCaseType}
                    userCostAccess={props.userSession.selectedOrg?.costAccess ?? null}
                    userSession={props.userSession}
                    renewalRegistrationActive={props.userSession.selectedOrg?.renewalRegistrationActive ?? false}
                />
            ) : (
                <Redirect to="/" />
            )}
        </>
    );
    return (
        <main className={classes.main}>
            <TopBar onDrawerExpanded={props.onDrawerExpandedMobile} /* pageTitle={pageTitle} */ caseTypeFilterConfig={caseTypeFilterConfig} />
            <div className={classes.content}>
                <Switch>
                    <Route exact path="/">
                        {dashBoard}
                    </Route>
                    <Route path="/dashboard">{dashBoard}</Route>
                    <Route path="/trademarks">
                        {props.userSession.selectedOrg && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Trademarks) ? (
                            <Trademarks caseType={CaseType.Trademark} verifiedUser={props.userSession.verifiedUser} />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/designs">
                        {props.userSession.selectedOrg && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Designs) ? (
                            <Designs caseType={CaseType.Design} verifiedUser={props.userSession.verifiedUser} />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/copyrights">
                        {props.userSession.selectedOrg && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Copyrights) ? (
                            <Copyrights caseType={CaseType.Copyright} verifiedUser={props.userSession.verifiedUser} />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/watches">
                        {props.userSession.selectedOrg && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Watches) ? (
                            <Watches caseType={CaseType.Watches} verifiedUser={props.userSession.verifiedUser} />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/renewals">
                        {props.userSession.selectedOrg && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Renewals) ? (
                            <Renewals
                                onCaseTypeFilterConfigurationCleared={onCaseTypeFilterCleared}
                                onCaseFilterKeyChanged={setCaseTypeFilterKey}
                                onRenewalsEntered={setRenewalsIsActive}
                                selectedCaseType={selectedCaseType}
                                userSession={props.userSession}
                                renewalRegistrationActive={props.userSession.selectedOrg?.renewalRegistrationActive ?? false}
                            />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/costs">
                        {userHasCostAccess(props.userSession.selectedOrg) &&
                        props.userSession.selectedOrg &&
                        !userIsReadOnly(props.userSession.verifiedUser) &&
                        userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Costs) ? (
                            <Costs
                                onCaseTypeFilterConfigurationCleared={onCaseTypeFilterCleared}
                                onCaseFilterKeyChanged={setCaseTypeFilterKey}
                                orgHasDisputes={orgHasDisputes}
                                selectedCaseType={selectedCaseType}
                                userCostAccess={props.userSession.selectedOrg?.costAccess ?? null}
                                userSession={props.userSession}
                                renewalRegistrationActive={props.userSession.selectedOrg?.renewalRegistrationActive ?? false}
                            />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/disputes">
                        {props.userSession.selectedOrg &&
                        !userIsReadOnly(props.userSession.verifiedUser) &&
                        userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.Disputes) ? (
                            <Disputes
                                onCaseTypeFilterConfigurationCleared={onCaseTypeFilterCleared}
                                onCaseFilterKeyChanged={setCaseTypeFilterKey}
                                onDisputesEntered={setDisputesIsActive}
                                selectedCaseType={selectedCaseType}
                                userSession={props.userSession}
                                renewalRegistrationActive={props.userSession.selectedOrg?.renewalRegistrationActive ?? false}
                            />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/admin" exact>
                        <Admin />
                    </Route>
                    <Route path="/admin/organisations">
                        <Organisations onSelectedOrgChanged={props.onSelectedOrgChanged} />
                    </Route>
                    <Route path="/admin/users">
                        <Users />
                    </Route>
                    <Route path="/price-calculator">
                        {!userIsReadOnly(props.userSession.verifiedUser) && userHasMenuAccess(props.userSession.verifiedUser, MenuAccess.PriceCalculator) ? (
                            <PriceCalculator
                                onCaseTypeFilterConfigurationCleared={onCaseTypeFilterCleared}
                                onCaseFilterKeyChanged={setCaseTypeFilterKey}
                                onPriceCalculatorEntered={setPriceCalculatorIsActive}
                                selectedCaseType={selectedCaseType}
                                userSession={props.userSession}
                                renewalRegistrationActive={props.userSession.selectedOrg?.renewalRegistrationActive ?? false}
                            />
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>
                    <Route path="/admin/collection-of-prices/trademarks">
                        <Surveys type={SurveyArea.Trademarks} />
                    </Route>
                    <Route path="/admin/collection-of-prices/designs">
                        <Surveys type={SurveyArea.Designs} />
                    </Route>
                    <Route path="/admin/collection-of-prices/watches">
                        <WatchPrices />
                    </Route>
                    <Route path="*">Page not found</Route>
                </Switch>
            </div>
        </main>
    );
}

export default Main;
